/**
 * 前端错误上报工具
 * 将浏览器端错误通过 POST /api/client-log 发送到后端，写入 PM2 日志
 */

interface ErrorReport {
  level?: 'error' | 'warn' | 'info';
  type?: string;
  message?: string;
  stack?: string;
  url?: string;
  method?: string;
  status?: number;
  userAgent?: string;
  extra?: unknown;
}

// 防抖：避免短时间内大量相同错误刷爆后端
const recentErrors = new Set<string>();
const DEDUP_WINDOW = 5000; // 5秒内相同错误只上报一次

// 批量队列：避免高频错误产生过多请求
let queue: ErrorReport[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;
const FLUSH_INTERVAL = 3000; // 3秒批量上报一次

function enqueue(report: ErrorReport) {
  // 去重
  const key = `${report.type}:${report.message}`;
  if (recentErrors.has(key)) return;
  recentErrors.add(key);
  setTimeout(() => recentErrors.delete(key), DEDUP_WINDOW);

  queue.push(report);

  if (!flushTimer) {
    flushTimer = setTimeout(flush, FLUSH_INTERVAL);
  }
}

async function flush() {
  flushTimer = null;
  if (queue.length === 0) return;
  const batch = queue;
  queue = [];

  try {
    await fetch('/api/error-reports', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(batch.length === 1 ? batch[0] : { type: 'batch', message: `${batch.length} errors`, extra: batch }),
      credentials: 'include',
    });
  } catch (e) {
    console.warn('[ErrorReport] 上报失败，可能网络不可达', e);
  }
}

export function reportError(report: ErrorReport) {
  if (!report.message) return;
  // 同时在浏览器 console 输出，方便开发调试
  if (report.level === 'warn') {
    console.warn(`[Frontend:${report.type}] ${report.message}`, report);
  } else {
    console.error(`[Frontend:${report.type}] ${report.message}`, report);
  }
  enqueue(report);
}
